import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import Icon from 'components/generic/Icon.component';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import canonicalizeSelectOptions from 'utils/react/canonicalizeSelectOptions.util';
import { capitalize } from 'utils/strings/manipulate.util';

import MultiSelectDropdownInputList from './MultiSelectDropdownInputList.component';

import CN from './MultiSelectDropdownInput.module.scss';

/**
 * MultiSelectDropdownInput component
 * @param {Object} params - Component props
 * @param {('left'|'right')} [params.align] - Dropdown alignment
 * @param {string} params.name - Input name (for onChange)
 * @param {function} [params.onChange] - Change handler, called with { name, value, raw }
 * @param {boolean} [params.disabled] - Whether the dropdown is disabled
 * @param {Array|Object} params.options - Options for the dropdown (array or object)
 * @param {Array|string} [params.value] - Currently selected value(s)
 * @param {number} [params.maxSelections] - Maximum number of selections allowed
 * @param {string} [params.separator] - Separator for displaying multiple selected items
 * @param {string} [params.placeholder] - Placeholder text when no selection
 * @param {string} [params.searchPlaceholder] - Placeholder for search input
 * @param {string} [params.noResultsText] - Text to show when search yields no results
 * @param {string} [params.selectedText] - Text to show in selection counter (e.g. "selected")
 * @returns {JSX.Element}
 */
export const MultiSelectDropdownInput = ({
  align,
  name,
  onChange = alwaysNoop,
  disabled,
  options: optionsFromProps,
  value: valueFromProps = [],
  maxSelections = 4,
  separator = '/',
  placeholder: placeholderFromProps,
  searchPlaceholder: searchPlaceholderFromProps,
  noResultsText: noResultsTextFromProps,
  selectedText: selectedTextFromProps,
  isButtonLargeHeight = false,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const placeholder = placeholderFromProps || t('multiSelectDropdown.placeholder');
  const searchPlaceholder = searchPlaceholderFromProps || t('multiSelectDropdown.searchPlaceholder');
  const noResultsText = noResultsTextFromProps || t('multiSelectDropdown.noResults');
  const selectedText = selectedTextFromProps || t('multiSelectDropdown.selected');

  const { val: isListRendered, tip: toggle, nay: close } = useTip(false);
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Transform options to canonical format
  const options = canonicalizeSelectOptions(optionsFromProps);

  // Local state for selected items (working copy before saving)
  const selectedValues = useMemo(() => asArray(valueFromProps), [valueFromProps]);
  const [tempSelected, setTempSelected] = useState(selectedValues);
  const [searchQuery, setSearchQuery] = useState('');

  useOnClickOutside(selectRef, close);

  // Handle focus and scroll behavior when dropdown opens
  useEffect(() => {
    if (isListRendered) {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      if (listRef.current) {
        elementScrollTo({ element: listRef.current, behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [isListRendered]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    toggle();
  }, [disabled, toggle]);

  const handleItemClick = useCallback(
    (item) => {
      const itemValue = item.extra || item.description;
      const isCurrentlySelected = tempSelected.includes(itemValue);

      let newSelection;
      if (isCurrentlySelected) {
        // Remove from selection
        newSelection = tempSelected.filter((val) => val !== itemValue);
      } else {
        // Add to selection if under max limit
        if (tempSelected.length < maxSelections) {
          newSelection = [...tempSelected, itemValue];
        } else {
          // Already at max, don't add
          return;
        }
      }

      setTempSelected(newSelection);

      // Auto-save on selection change
      onChange({
        name,
        value: newSelection,
        raw: newSelection,
      });
    },
    [tempSelected, maxSelections, onChange, name],
  );

  const handleSearchChange = useCallback((ev) => {
    setSearchQuery(ev.target.value);
  }, []);

  // Filter options based on search query
  const filteredOptions = searchQuery ? options.filter((opt) => opt.description.toLowerCase().includes(searchQuery.toLowerCase())) : options;

  const buttonClasses = cn({
    [CN.selectButton]: true,
    [CN.buttonLargeHeight]: isButtonLargeHeight,
    [CN.isOpen]: isListRendered,
  });

  const dropdownClasses = cn({
    [CN.list]: true,
    [CN[`${align}Align`]]: align,
  });

  // Display value: join selected items with separator
  const displayValue =
    selectedValues.length > 0
      ? selectedValues
          .map((val) => {
            const opt = options.find((o) => (o.extra || o.description) === val);
            const text = opt ? opt.description : val;
            return capitalize(fromAnyToString(text));
          })
          .join(separator)
      : placeholder;

  const isShowingButtonPlaceholder = selectedValues.length === 0;

  const isMaxReached = tempSelected.length >= maxSelections;

  return (
    <div
      ref={selectRef}
      data-bem="MultiSelectDropdownInput"
      data-name={name}
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
      role="combobox"
      aria-haspopup="dialog"
      aria-expanded={isListRendered}
      aria-controls={isListRendered ? `${name}-listbox` : undefined}
      aria-label={placeholder}
    >
      <button
        data-bem="MultiSelectDropdownInput__button"
        className={buttonClasses}
        type="button"
        tabIndex={0}
        disabled={disabled}
        onClick={handleToggle}
        aria-label={selectedValues.length > 0 ? `${selectedValues.length} ${selectedText}. ${placeholder}` : placeholder}
      >
        <span
          className={cn({
            [CN.displayValue]: true,
            [CN.placeholder]: isShowingButtonPlaceholder,
          })}
          aria-hidden="true"
        >
          {displayValue}
        </span>
        <Icon variant="arrowDown" width="lg" className={CN.dropdownArrow} isCentered aria-hidden="true" />
      </button>
      {isListRendered && (
        <MultiSelectDropdownInputList
          id={`${name}-listbox`}
          dropdownClasses={dropdownClasses}
          listRef={listRef}
          searchInputRef={searchInputRef}
          searchPlaceholder={searchPlaceholder}
          searchQuery={searchQuery}
          handleSearchChange={handleSearchChange}
          tempSelected={tempSelected}
          maxSelections={maxSelections}
          selectedText={selectedText}
          filteredOptions={filteredOptions}
          noResultsText={noResultsText}
          isMaxReached={isMaxReached}
          handleItemClick={handleItemClick}
          selectedValues={selectedValues}
          setTempSelected={setTempSelected}
          setSearchQuery={setSearchQuery}
        />
      )}
    </div>
  );
};
